import { Injectable } from '@angular/core';
import { SystemsService } from '@app/core/services/systems.service';
import { System as LegacySystem } from '@app/core/resources/system';
import { DocumentCollection } from 'ngx-jsonapi';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { SystemsRepositoryPort } from 'common/systems/application/ports/systems-repository.port';
import { System } from 'common/systems/domain/resources/system.resource';
import { mapLegacySystemToCommon } from 'ui/features/systems/infrastructure/adapters/map-legacy-system';

/**
 * Implements `SystemsRepositoryPort` over the existing legacy
 * `SystemsService`, mapping its `DocumentCollection<System>` to a
 * `ReadonlyArray<System>` of the common resource via
 * `mapLegacySystemToCommon`.
 *
 * ngx-jsonapi's `all()` first emits the copy it keeps in cache (memory or IndexedDB) while the
 * request is still on its way (`is_loading`), and then the server's answer. That copy can be
 * incomplete (rates without values, systems missing), and the deals amounts calculated from it
 * show ∞ until the answer arrives, so only finished collections are returned.
 */
@Injectable({ providedIn: 'root' })
export class SystemsRepositoryAdapter implements SystemsRepositoryPort {
    public constructor(private readonly systemsService: SystemsService) {}

    public getAll(): Observable<ReadonlyArray<System>> {
        return this.systemsService.all({ include: ['rates', 'currency', 'networks'] }).pipe(
            filter(
                (collection: DocumentCollection<LegacySystem>): boolean => !collection.is_loading,
            ),
            map(
                (collection: DocumentCollection<LegacySystem>): ReadonlyArray<System> =>
                    collection.data.map(
                        (legacySystem: LegacySystem): System =>
                            mapLegacySystemToCommon(legacySystem),
                    ),
            ),
        );
    }
}
